/* External */
import {
  useEffect, useMemo, memo, useState, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Map,
  List,
  OrderedMap,
  Record,
} from 'immutable';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation } from 'react-router-dom';

/* Ott-common */
import Skeleton from '@dtvgo/skeleton';

/* Providers */
import { useCurrentTime } from '../../../providers/CurrentTimeProvider';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';

/* Components */
import MiniEpgChannelsContainer from './MiniEpgChannelsContainer';
import VirtualizedList, { ItemDataProvider } from '../../VirtualizedList';
import MiniEpgChannelLane from './MiniEpgChannelLane';
import MiniEpgFilterList from './MiniEpgFiltersList';
import MiniEpgDetailCard from './MiniEpgDetailCard/MiniEpgDetailCard';
import MiniEpgEmptyChannelMessage from './MiniEpgEmptyChannelMessage';

/* Utils */
import useSortAndFilterChannels from '../../../hooks/useSortAndFilterChannels';
import useHandleThrottleArrowPress from '../../../hooks/useHandleThrottleArrowPress';
import { sizeByViewport } from '../../../utils/cardsUtils';
import { Direction } from '../../../utils/spatialNavigation';
import { noOp } from '../../../utils/functions';
import { useOnBackPress } from '../../../utils/keyDownEvents';
import { EPG_FILTERS_ID } from '../../../utils/constants';
import { COMPONENTS } from '../../../utils/focusKey';
import useCurrentSchedule from '../../../hooks/useCurrentSchedule';
import { useCountry } from '../../../providers/ConfigProvider';
import { isSports } from '../../../utils/metaDataUtils';
import {
  formatContentData,
  getLiveStartProgressBar,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../../utils/googleTags';

/* Styles */
import './MiniEpg.scss';

const miniEpgHeight = sizeByViewport(1080 - 324 - 96);
const CHANNEL_LANE_HEIGHT = sizeByViewport(115);
const NUMBER_OF_CARDS = 7;

function MiniEpg({
  className,
  show,
  currentChannelId,
  hide,
  resetScrollPosition,
  setFocus,
  channels,
  selectedFilter,
  handleFilterSelect,
  isFetching,
  filtersMiniEpg,
  homeOnBack,
  currentChannelName,
  currentContent,
}) {
  const { state: { transmissionType } = {} } = useLocation();
  const { commonTaggingEvent, gtmChangeItem } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const currentTime = useCurrentTime();
  const isFilterSelectedRef = useRef(false);
  const isFilterCategoryRef = useRef(true);
  const selectedFilterIdRef = useRef(selectedFilter?.id);
  const country = useCountry();
  const [wasMiniEpgOpened, setWasMiniEpgOpened] = useState(false);
  const [focusedSchedule, setFocusedSchedule] = useState(undefined);
  const [showGradient, setShowGradient] = useState(false);

  const handleThrottleArrowPress = useHandleThrottleArrowPress(undefined, 140);

  useOnBackPress({ listener: useCallback(() => hide(true), [hide]), when: show });

  useEffect(() => {
    if (show) setWasMiniEpgOpened(true);
  }, [show]);

  const filtersSeq = useMemo(() => filtersMiniEpg.toIndexedSeq(), [filtersMiniEpg]);
  const selectedFilterIndex = useMemo(
    () => filtersSeq.indexOf(selectedFilter),
    [filtersSeq, selectedFilter],
  );

  const handleListPosition = useCallback(() => selectedFilterIndex + 1, [selectedFilterIndex]);

  const miniEpgCommonTagEvent = useCallback(({ eventName, extraData }) => {
    const content = extraData?.newContent || currentContent;
    const contentTimes = getLiveStartProgressBar({
      startTime: extraData?.startTime,
      endTime: extraData?.endTime,
      currentTime,
    });
    const durationInSeconds = contentTimes?.duration ? contentTimes.duration * 60 : 0;
    commonTaggingEvent({
      category: GTM_CATEGORIES.EPG_DATA,
      subCategory: GTM_SUBCATEGORIES.EPG.EPG_LIVE_MINI_EPG,
      eventName,
      data: {
        content: {
          ...formatContentData({
            content,
            rest: {
              channelId: extraData?.newChannelId || currentChannelId,
              channelName: extraData?.newChannelName || currentChannelName,
              isLive: true,
              progress: contentTimes?.startProgressBar,
              startProgressBar: contentTimes?.startProgressBar,
              duration: durationInSeconds,
            },
          }),
        },
        filterMiniEpg: extraData?.filterId,
        transmissionType: transmissionContentType(transmissionType),
        playerType: playerType(content?.type, true, isSports(content)),
        isSports: isSports(content),
        isLive: true,
        listPosition: extraData?.listPosition || handleListPosition(),
      },
    });
  }, [
    commonTaggingEvent,
    currentChannelId,
    currentChannelName,
    currentContent,
    currentTime,
    handleListPosition,
    transmissionType,
  ]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      miniEpgCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [handleListPosition, miniEpgCommonTagEvent, country]);

  const handleFilterCategoryEvent = useCallback(() => {
    if (isFilterCategoryRef.current && selectedFilter?.id) {
      miniEpgCommonTagEvent({
        eventName: GTM_EVENT_NAMES.EPG.FILTER_CATEGORY,
        extraData: { filterId: selectedFilter?.id },
      });
      isFilterCategoryRef.current = false;
    }
  }, [selectedFilter, miniEpgCommonTagEvent]);

  useEffect(() => {
    const checkSelectedFilter = (isFilterSelectedRef.current || selectedFilter?.id?.toLowerCase() !== 'for-you');
    if ((checkSelectedFilter && selectedFilterIdRef.current === selectedFilter?.id)
      || checkSelectedFilter) {
      handleFilterCategoryEvent();
      isFilterSelectedRef.current = false;
    }
  }, [handleFilterCategoryEvent, selectedFilter?.id]);

  const handleFilterSelectWithTagEvent = useCallback((filterId) => {
    handleFilterSelect(filterId);
    selectedFilterIdRef.current = filterId;
    isFilterSelectedRef.current = true;
    isFilterCategoryRef.current = true;
  }, [handleFilterSelect]);

  const handleFilterArrowPress = useCallback(() => {
    if (!isFilterSelectedRef.current) {
      isFilterSelectedRef.current = true;
      isFilterCategoryRef.current = true;
    }
  }, []);

  const handleChannelArrowPress = useCallback((direction, { index }) => {
    let newFilterSelectedIndex;

    if (direction === Direction.RIGHT && selectedFilterIndex + 1 < filtersMiniEpg?.size) {
      newFilterSelectedIndex = selectedFilterIndex + 1;
    } else if (direction === Direction.LEFT && selectedFilterIndex - 1 >= 0) {
      newFilterSelectedIndex = selectedFilterIndex - 1;
    }

    if (newFilterSelectedIndex !== undefined) {
      handleFilterSelect(filtersSeq.get(newFilterSelectedIndex).id);
      setFocus(`${COMPONENTS.MINI_EPG_CARD}0`);
      handleFilterArrowPress();
      return false;
    }

    if (direction === Direction.LEFT && selectedFilterIndex === 0) {
      hide(true);
      return false;
    }

    if (direction === Direction.UP && index === 0 && selectedFilter) {
      setFocus(`${COMPONENTS.MINI_EPG_FILTER_LIST}${selectedFilter?.id}`);
      return false;
    }

    return handleThrottleArrowPress();
  }, [selectedFilterIndex,
    filtersMiniEpg?.size,
    handleThrottleArrowPress,
    handleFilterSelect,
    filtersSeq,
    setFocus,
    hide,
    selectedFilter,
    handleFilterArrowPress,
  ]);

  const sortedChannels = useSortAndFilterChannels(
    channels,
    selectedFilter?.id === EPG_FILTERS_ID.FOR_YOU,
  );

  const channelsToRender = useMemo(() => sortedChannels?.toList(), [sortedChannels]);

  const firstChannel = useMemo(() => channelsToRender?.get(0), [channelsToRender]);

  const { schedule: firstChannelCurrentSchedule, isCompleted } = useCurrentSchedule(firstChannel);

  useEffect(() => {
    if (!selectedFilter?.id) {
      setFocusedSchedule(undefined);
    }
  }, [selectedFilter]);

  useEffect(() => {
    gtmChangeItem({ miniEpgLiveTitle: selectedFilter?.id });
  }, [gtmChangeItem, selectedFilter?.id]);

  useEffect(() => {
    if ((channelsToRender?.size === 0 || !channelsToRender?.size) && !isFetching) {
      setFocus(`${COMPONENTS.MINI_EPG_FILTER_LIST}${selectedFilter?.id}`);
    } else {
      setFocus(`${COMPONENTS.MINI_EPG_CARD}0`);
    }
  }, [channelsToRender?.size, isFetching, selectedFilter?.id, setFocus]);

  const focusedScheduleToUse = focusedSchedule ?? firstChannelCurrentSchedule;

  const handleScheduleBecameFocused = useCallback((_, { index, schedule }) => {
    if (schedule) {
      setFocusedSchedule(schedule);
    }
    if (index > 2 && (index < (channelsToRender.size - 3))) {
      setShowGradient(true);
    } else {
      setShowGradient(false);
    }
  }, [channelsToRender]);

  const handleScheduleMouseEnter = useCallback(({ schedule }) => {
    if (schedule) {
      setFocusedSchedule(schedule);
    }
  }, []);

  const itemData = useMemo(() => ({
    channels: channelsToRender,
    handleChannelArrowPress,
    currentChannelId,
    hideMiniEpg: hide,
    handleScheduleBecameFocused,
    handleScheduleMouseEnter,
    homeOnBack,
    focusedScheduleToUse,
    setFocusedSchedule,
    miniEpgCommonTagEvent,
  }), [
    channelsToRender,
    handleChannelArrowPress,
    currentChannelId,
    hide,
    handleScheduleBecameFocused,
    handleScheduleMouseEnter,
    homeOnBack,
    focusedScheduleToUse,
    setFocusedSchedule,
    miniEpgCommonTagEvent,
  ]);

  const isForYouCategory = selectedFilter?.id === EPG_FILTERS_ID.FOR_YOU;
  const showSkeletons = isFetching || !isCompleted;

  const miniEpgContent = !!channelsToRender && channelsToRender.size > 0 ? (
    <ItemDataProvider itemData={itemData}>
      <VirtualizedList
        className={`${showGradient ? 'dtv-html5-c-mini-epg__channels-container--show-gradient' : ''}`}
        outerElementType={MiniEpgChannelsContainer}
        height={miniEpgHeight}
        itemCount={channelsToRender?.size || 0}
        itemSize={CHANNEL_LANE_HEIGHT}
        renderer={MiniEpgChannelLane}
        resetScrollPosition={resetScrollPosition}
      />
    </ItemDataProvider>
  ) : (
    <MiniEpgEmptyChannelMessage
      emptyChannelsMessage={selectedFilter?.emptyMessages}
      isForYouCategory={isForYouCategory}
    />
  );

  return (
    <div
      className={`dtv-html5-c-mini-epg ${show ? 'dtv-html5-c-mini-epg--shown' : (wasMiniEpgOpened && 'dtv-html5-c-mini-epg--hidden')
      } ${className}`}
    >
      {showSkeletons ? (
        <Skeleton className="dtv-html5-c-mini-epg__detail-card__skeleton" dark />
      ) : (
        <MiniEpgDetailCard focusedSchedule={focusedScheduleToUse} />
      )}
      <MiniEpgFilterList
        filters={filtersMiniEpg}
        selectedFilterId={selectedFilter?.id}
        onFilterSelect={handleFilterSelectWithTagEvent}
        hideMiniEpg={hide}
        selectedFilterIndex={selectedFilterIndex}
      />
      {showSkeletons ? (
        <div className="dtv-html5-c-mini-epg__channels-container">
          {Array.from({ length: NUMBER_OF_CARDS }).fill().map((value, index) => (
            <Skeleton
              // eslint-disable-next-line react/no-array-index-key -- only presentational
              key={index}
              className="dtv-html5-c-mini-epg__channel-lane-skeleton"
              dark
            />
          ))}
        </div>
      ) : miniEpgContent}
    </div>
  );
}

MiniEpg.propTypes = {
  className: PropTypes.string,
  hide: PropTypes.func,
  show: PropTypes.bool,
  currentChannelId: PropTypes.string,
  resetScrollPosition: PropTypes.bool,
  setFocus: PropTypes.func.isRequired,
  channels: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(List),
  ]),
  selectedFilter: PropTypes.instanceOf(Record),
  handleFilterSelect: PropTypes.func,
  isFetching: PropTypes.bool,
  filtersMiniEpg: PropTypes.instanceOf(OrderedMap),
  homeOnBack: PropTypes.bool,
  currentChannelName: PropTypes.string,
  currentContent: PropTypes.instanceOf(Object),
};

MiniEpg.defaultProps = {
  className: '',
  hide: noOp,
  show: false,
  currentChannelId: undefined,
  resetScrollPosition: false,
  channels: undefined,
  selectedFilter: undefined,
  handleFilterSelect: noOp,
  isFetching: false,
  filtersMiniEpg: undefined,
  homeOnBack: false,
  currentContent: undefined,
  currentChannelName: undefined,
};

export default withFocusable()(memo(MiniEpg));
